<template>
	<div class="pb-2">
		<div class="head-title px-6">
			<ReceiptButton 
				:control="control"
				:privileges="privileges"
				@delete="delete_confirm_visible = true" 
				@create="onCreateClaims()" 
				@update="$emit('update')" />
			<ReceiptView
				:typeSelected="type"
				:viewSelected="view"
				:statuses="receipt_type"
				:customise_view="customise_view"
				@status="onChangeStatus($event)"
				@column="onChangeColumn($event)" />
		</div>
		<v-alert class="mx-5 mt-3" type="error" v-if="error_msg.length !== 0">
			{{ error_msg }}
		</v-alert>
		<ProgressGetDataLinear v-if="loading" class="px-5"/>
		<ReceiptTable
			v-if="!loading" 
			class="ma-6"
			:header="table_header"
			:items="filterResults"
			:color="color" 
			:currencies="currencies" 
			:edit_type="edit_type"
			:edit_control="edit_control"
			:privileges="privileges"
			@error-msg="error_msg = $event"
			@button-visible="control = $event"
			@show-file="onShowFile($event)" 
			@row-click="onRowClick()"
			@edit-visible="onEditVisible($event)"
			@update="onUpdate($event)"/>
		<ReceiptImageView 
			v-if="image_visible"
			:visible="image_visible" 
			:show_image="show_image"
			@image-close="onImageClose()"
		/>
		<DeleteComfirmDialog
			v-if="delete_confirm_visible" 
			:visible="delete_confirm_visible"
			:title="`Do you wish to delete expenses`"
			:content="`This action cannot be undone. Are you sure?`"
			:okText="`Delete`"
			@cancel="delete_confirm_visible = false"
			@ok="onDelReceipts()"
		/>
	</div>
</template>

<script>
import ReceiptView from "@/views/expenses/receipts/receipt_view.vue";
import ReceiptButton from "@/views/expenses/receipts/receipt_button.vue";
import ReceiptImageView from "@/views/expenses/receipts/receipt_image_view.vue";
import ReceiptTable from "@/views/expenses/receipts/receipt_table.vue";
import ProgressGetDataLinear from "@/components/progress/progress_getdata_linear.vue";
import DeleteComfirmDialog from "@/views/expenses/commond/delete_confirm_dialog.vue";

import Receipt from "@/models/receipt";
import config from "@/config.js";

const RECEIPT_TYPE = [
	{ text: 'All expenses', value: 'ar' },
	{ text: 'With receipts', value: 'wr' },
	{ text: 'Without receipts', value: 'nr' },
	{ text: 'Incomplete expenses', value: 'ir' },
	{ text: 'Completed expenses', value: 'cr' }
];

const EDIT_TYPE = {
	date: 'date',
	currency: 'currency',
	value: 'value',
	kind: 'kind',
	code: 'code',
	title: 'title',
	desc: 'description',
	phase: 'phase'
};

const EDIT_CONTROL = {
	menu_date: false,
	edit_date_visible: 0,
	edit_currency_visible: 0,
	edit_value_visible: 0,
	edit_kind_visible: 0,
	edit_code_visible: 0,
	edit_title_visible: 0,
	edit_desc_visible: 0,
	edit_phase_visible: 0,
};

const SEC = 'Sec';

const RECEIPT_VIEW_VERSION = 2;
const RECEIPT_VIEW_V2_COLUMNS = [
	'tax_amount',
	'amount',
	'markup_perc',
	'markup_value'
];

export default {
	name: 'ListReceipt',
	props: {
		color: {
			type: String,
			default: ""
		},
		currencies: {
			type: Array,
			default: () => {},
		},
	},
	components: {
		ReceiptView,
		ReceiptButton,
		ReceiptImageView,
		ReceiptTable,
		ProgressGetDataLinear,
		DeleteComfirmDialog
	},
	data() {
		this.sec = SEC;
		return {
			user_id: 0,
			loading: false,
			error_msg: '',
			control: false,
			receipts_original: [],
			receipts: [],
			table_header: config.receipts_head.filter(head => {return head.showable}),
			receipt_type: RECEIPT_TYPE,
			customise_view: config.receipts_head.filter(head => {return head.showable && head.value !== 'select'}),
			
			type: RECEIPT_TYPE[0].value,
			type_save: RECEIPT_TYPE[0].value,
			view: [],
			view_save: [],

			edit_type: EDIT_TYPE,
			edit_control: EDIT_CONTROL,
			
			image_visible: false,
			show_image: null,
			delete_confirm_visible: false,
		}
	},
	async created() {
		if (!this.$store.state.user) {
			await this.getUserId();
		} else {
			this.user_id = this.$store.state.user.id;
		}
		
		if (this.$store.state.privileges && this.$store.state.privileges.length === 0) {
			this.initPrivileges();
		}

		await this.getSettings();

		this.load_receipts();
	},
	watch: {
		user_abid() {
			if (this.user_abid !== 0) {
				this.load_receipts();
			}
		},
	},
	computed: {
		privileges() {
			return this.$store.state.privileges.filter(p => { return p.startsWith('S')});
		},
		user_abid() {
			let user = this.$store.state.user;
			if (user==null){
				return 0;
			} else if (user.addressbook==null){
				return 0;
			} else { 
				return user.addressbook.id
			}
		},
		filterResults() {
			const filterRes = [];

			this.receipts.forEach(receipt => {
				const obj = {};	
				const header_tmp = config.receipts_head.filter(head => {return head.showable});

				header_tmp.forEach(item => {
					if (item.showable) {
						obj[item.value] = receipt[item.value];
					}
				});

				obj['id']	= receipt.id;
				obj['select']	= receipt.select ? receipt.select : false;
				obj['code']	= receipt.job;
				obj['title'] = receipt.job;
				obj['employee_id'] = receipt.employee_id;
				obj['description'] = receipt.description;
				obj['phase'] = receipt.phase;
				obj['phases'] = receipt.phases ? receipt.phases : [];
				obj['has_tax'] = receipt.has_tax;
				obj['image_type'] = receipt.image_type;
				obj['amount'] = Number((Number(receipt.value) + Number(receipt.tax_amount)).toFixed(2));
				obj['tax_mode'] = receipt.tax_mode;
				obj['tax_perc'] = receipt.tax_perc;
				obj['tax_amount'] = receipt.tax_amount;
				obj['markup_perc'] = receipt.markup_perc;
				obj['markup_value'] = receipt.markup_value;

				filterRes.push(obj);
			});

			return filterRes;
		},
	},
	methods: {
		async getUserId() {
			const url = '/api/user/me';
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						this.user_id = data.id;
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
				});
		},
		initPrivileges() {
			this.$store.dispatch('update_privileges');
		},
		async getSettings() {
			this.loading = true;
			const usid = this.user_id;
			const winname_tmp = this.$route.name;
			const winname = config.win_name[`${winname_tmp}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;
			await axios
				.get(url, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						if (data.length !== 0) {
							const current_settings = data.filter(d => { return d.user_id === usid });
							const settings_winname = current_settings[0].window_name;
							const settings_windata_json = current_settings[0].windata_json;
							if (settings_winname === winname) {
								const windata = JSON.parse(settings_windata_json);
								const saved_version = Number(windata.version || 1);
								const saved_options = Array.isArray(windata.options) ? windata.options : [];
								const options = saved_version < RECEIPT_VIEW_VERSION
									? [...new Set([...saved_options, ...RECEIPT_VIEW_V2_COLUMNS])]
									: saved_options;

								this.type = windata.type;
								this.view = options;

								this.type_save = windata.type;
								this.view_save = options;

								if (saved_version < RECEIPT_VIEW_VERSION) {
									this.updateSettings(this._setWinSetup()).catch(reason => {
										this.error_msg = reason;
									});
								}
							}
						} else {
							const views = config.receipts_head.filter(head => {return head.showable && head.value !== 'select'});
							this.view = views.map(v => { return v.value });
							this.view_save = views.map(v => { return v.value });
						}

						this.initTableHead();

						this.loading = false;
					} else {
						this.error_msg = reason;
						this.loading = false;
					}
				})
				.catch((e) => {
					this.error_msg = e.message;
					this.loading = false;
				});
		},
		updateSettings(win_setup) {
			const usid = this.user_id;
			const winname_tmp = this.$route.name;
			const winname = config.win_name[`${winname_tmp}`];

			const url = `/api/user/${usid}/webwinsetup/${winname}`;

			const params_obj = {};
			params_obj['value'] = win_setup;

			return new Promise((resolve, reject) => {
				axios
					.post(url, params_obj, {
						headers: {
							"Authorization": "Bearer " + this.$store.state.token,
							"Accept": "application/json"
						}
					})
					.then((val) => {
						const json = val.data;

						const { status, data, reason } = json;

						if (status == "OK") {
							resolve(data);
						} else {
							reject(reason);
						}
					})
					.catch((e) => {
						reject(e.message);
					});
			});			
		},
		initTableHead() {
			const head_seleced = config.receipts_head.filter(head => { return head.showable && head.value === 'select'});
			const head_other = config.receipts_head.filter(head => { return this.view.includes(head.value) });
			
			this.table_header = [...head_seleced, ...head_other];
		},
		load_receipts() {
			let user_abid = this.user_abid;
			this.loading = true;
			if (user_abid != 0) {
				// temporarily create a new receipt to get access to our list logic
				const receipt = new Receipt();
			
				receipt.list(user_abid)
					.then(data => {
						const claimed_arr = data.filter(d => {return d.is_used});
						claimed_arr.sort((a, b) => {
							const date1 = Date.parse(a.date);
							const date2 = Date.parse(b.date);
							return date2 - date1;
						});

						const other_arr = data.filter(d => {return !d.is_used});
						other_arr.sort((a, b) => {
							const date1 = Date.parse(a.date);
							const date2 = Date.parse(b.date);
							return date2 - date1;
						});

						this.receipts_original = [];
						this.receipts_original.push(...other_arr);
						this.receipts_original.push(...claimed_arr);

						this.receipts = this.receipts_original;
						this._filterReceipt(this.type);
						
						this.loading = false;
					})
					.catch(e => {
						this.loading = false;
						this.error_msg = e;
					});
			}
		},
		onChangeStatus(status) {
			this._filterReceipt(status);

			const status_arr = RECEIPT_TYPE.map(r => { return r.value });
			if (status_arr.includes(status)) {
				this.type_save = status;
				const win_setup_str = this._setWinSetup();

				this.updateSettings(win_setup_str)
					.then(() => {
					})
					.catch((reason) => {
						this.error_msg = reason;
					});
			}
		},
		onChangeColumn(column) {
			const head_seleced = config.receipts_head.filter(head => { return head.showable && head.value === 'select'});
			const head_other = config.receipts_head.filter(head => { return column.includes(head.value) });
			this.table_header = [...head_seleced, ...head_other];

			this.view_save = column;
			const win_setup_str = this._setWinSetup();

			this.updateSettings(win_setup_str)
				.then(() => {
				})
				.catch((reason) => {
					this.error_msg = reason;
				});
		},
		onShowFile(show_image) {
			this.show_image = show_image;
			this.image_visible = true;
		},
		onImageClose() {
			this.image_visible = false;
			URL.revokeObjectURL(this.show_image.image);
		},
		onRowClick() {
			this.edit_control.menu_date = false;
			this.edit_control.edit_date_visible = 0;
			this.edit_control.edit_currency_visible = 0;
			this.edit_control.edit_value_visible = 0;
			this.edit_control.edit_kind_visible = 0;
			this.edit_control.edit_code_visible = 0;
			this.edit_control.edit_title_visible = 0;
			this.edit_control.edit_desc_visible = 0;
			this.edit_control.edit_phase_visible = 0;
		},
		async onEditVisible(event) {
			if (!this.privileges.includes(this.sec)) return;

			if (event.type === this.edit_type.date) {
				this.edit_control.edit_date_visible = event.id;
			}
			if (event.type === this.edit_type.currency) {
				this.edit_control.edit_currency_visible = event.id;
			}
			if (event.type === this.edit_type.value) {
				this.edit_control.edit_value_visible = event.id;
			}
			if (event.type === this.edit_type.kind) {
				this.edit_control.edit_kind_visible = event.id;
			}
			if (event.type === this.edit_type.code) {
				this.edit_control.edit_code_visible = event.id;
			}
			if (event.type === this.edit_type.title) {
				this.edit_control.edit_title_visible = event.id;
			}
			if (event.type === this.edit_type.desc) {
				this.edit_control.edit_desc_visible = event.id;
			}
			if (event.type === this.edit_type.phase) {
				const receipt =  this.receipts.filter(r => r.id === event.id);

				if (receipt[0].job) {
					await this._getPhase(receipt[0].job.id, receipt[0].id);
				}

				this.edit_control.edit_phase_visible = event.id;
			}
		},
		onUpdate(event) {
			const event_obj = event.obj;
			const url = "/api/expenses/receipts/" + event_obj.id;

			const param_obj = {
				employee_id: event_obj.employee_id,
				desc: event_obj.desc,
				date: event_obj.date,
				currency_id: event_obj.currency_id,
				gross: Number(Number(event_obj.value).toFixed(2)),
				has_tax: event_obj.has_tax === true ? 1: 0, /* seems it doesn't support boolean here */
				job_id: event_obj.job_id,
				phase_id: event_obj.phase_id,
				kind_id: event_obj.kind_id,
				tax_mode: event_obj.tax_mode,
				tax_perc: Number(event_obj.tax_perc),
				tax_amount: Number(Number(event_obj.tax_amount).toFixed(2)),
				markup_perc: Number(event_obj.markup_perc),
				value: Number(Number(event_obj.markup_value).toFixed(2)),
			};

			axios
				.put(url, param_obj, {
					headers: {
						"Authorization": "Bearer " + this.$store.state.token,
						"Accept": "application/json"
					}
				})
				.then((val) => {
					const {status, data, reason} = val.data;

					if (status == "OK") {
						const select_arr = this.filterResults.filter(filterResult => {return !filterResult.is_used && filterResult.select === true});
						const select_ids = select_arr.map(arr => { return arr.id });
						this.onJobChange(event.type, select_ids, data);
					} else {
						this.error_msg = reason;
					}
				})
				.catch((e) => {
					this.error_msg = e;
				});
		},
		onJobChange(type, select_ids, item) {
			if (type === this.edit_type.date) {
				this.edit_control.menu_date = false;
				this.edit_control.edit_date_visible = 0;	
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						receipt.date = item.date;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});					
			}
			if (type === this.edit_type.currency) {
				this.edit_control.edit_currency_visible = 0;	
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						receipt.currency = item.currency;
						receipt.tax_perc = item.tax_perc;
						receipt.tax_amount = item.tax_amount;
						receipt.amount = item.amount;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});					
			}
			if (type === this.edit_type.value) {
				this.edit_control.edit_value_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						receipt.value = item.value;
						receipt.tax_amount = item.tax_amount;
						receipt.amount = item.amount;
						receipt.markup_value = item.markup_value;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
			if (type === this.edit_type.kind) {							
				this.edit_control.edit_kind_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						const obj = JSON.parse(JSON.stringify(item.kind));
						receipt.kind = obj;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
			if (type === this.edit_type.code) {
				this.edit_control.edit_code_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						const obj = JSON.parse(JSON.stringify(item.job));
						receipt.job = obj;
						receipt.phase = item.phase;
						receipt.kind = item.kind;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
			if (type === this.edit_type.title) {
				this.edit_control.edit_title_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						const obj = JSON.parse(JSON.stringify(item.job));
						receipt.job = obj;
						receipt.phase = item.phase;
						receipt.kind = item.kind;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
			if (type === this.edit_type.desc) {
				this.edit_control.edit_desc_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						receipt.description = item.desc;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
			if (type === this.edit_type.phase) {
				this.edit_control.edit_phase_visible = 0;
				this.receipts.forEach(receipt => {
					if (receipt.id === item.id) {
						const obj = JSON.parse(JSON.stringify(item.phase));
						receipt.phase = obj;
						receipt.kind = item.kind;
					} 
					if (select_ids.includes(receipt.id)) {
						receipt.select = true;
					}
				});
			}
		},
		onDelReceipts() {
			this.filterResults.forEach(res => {
				if (res.select) {
					const receipt_record = new Receipt();

					// it's enough to just use the id
					receipt_record.id = res.id;

					// and delete it
					receipt_record.delete()
						.then(() => {
							this.receipts.forEach((receipt, index) => {
								if (receipt.id === res.id) {
									if (index >= 0) {
										this.receipts.splice(index, 1)
									}
									this.control = this.filterResults.some(res => { 
										return res.select === true
									});
								}
							});

							this.delete_confirm_visible = false;
						})
						.catch(e => {
							this.error_msg = e.message;
						})
				}
			});
		},
		onCreateClaims() {
			const ids = [];
			this.filterResults.forEach(res => {
				if (res.select) {
					res.select = false;
					ids.push(res.id);
				}
			});
			
			this.control = false;
			this.$emit('create-claims', ids);
		},
		_filterReceipt(status) {
			if (status === RECEIPT_TYPE[0].value) {
				this.receipts = this.receipts_original;
			}
			if (status === RECEIPT_TYPE[1].value) {
				this.receipts = this.receipts_original.filter(receipt => {
					return receipt.image_type.length !== 0;
				});
			}
			if (status === RECEIPT_TYPE[2].value) {
				this.receipts = this.receipts_original.filter(receipt => {
					return receipt.image_type.length === 0;
				});
			}
			if (status === RECEIPT_TYPE[3].value) {
				this.receipts = this.receipts_original.filter(receipt => {
					return !receipt.is_used;
				});
			}
			if (status === RECEIPT_TYPE[4].value) {
				this.receipts = this.receipts_original.filter(receipt => {
					return receipt.is_used;
				});
			}
		},
		async _getPhase(job_id, receipt_id) {
			await axios.get('/api/jobs/' + job_id + '/phases', {
				headers: {
					"Authorization": "Bearer " + this.$store.state.token,
					"Accept": "application/json"
				}
			})
				.then(val => {
					const { status, data, reason } = val.data;

					if (status == "OK") {
						const receipts_tmp = JSON.parse(JSON.stringify(this.receipts));
						receipts_tmp.forEach(r => {
							if (r.id === receipt_id) {
								r.phases = data;
								if (!r.phase) {
									const phases_arr = data;
									if (phases_arr.length !== 0) {
										r.phase = phases_arr[0];
									} else {
										r.phase = null;
									}
								}
							}
						});	
						this.receipts = receipts_tmp;

					} else {
						this.error_msg = reason;
					}

				})
				.catch(msg => {
					this.error_msg = msg;
				})
		},
		_setWinSetup() {
			const win_setup_obj = {
				version: RECEIPT_VIEW_VERSION,
				type: this.type_save,
				options: this.view_save,
			};

			const win_setup_str = JSON.stringify(win_setup_obj);
			return win_setup_str;
		}
	}
}
</script>

<style lang="scss" scoped>
.head-title {
  height: 68px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  background: #FFFFFF;
  box-shadow: inset 0px 3px 5px rgba(0, 0, 0, 0.1);
}
</style>